import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getMyProfile } from "../services/UsersService";
import { getAuthToken, setOrgFeatures } from "../services/authService";

export interface APIUser {
  id: string;
  organizationId?: string;
  organization_id?: string;
  orgId?: string;
  fullName: string;
  email?: string;
  phone?: string;
  country?: string;
  banner?: string;
  active: boolean;
  role?: string;
}

type UserContextType = {
  user: APIUser | null;
  setUser: React.Dispatch<React.SetStateAction<APIUser | null>>;
  refreshUser: () => Promise<void>;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return ctx;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<APIUser | null>(null);

  const refreshUser = useCallback(async () => {
    if (!getAuthToken()) {
      setUser(null);
      return;
    }
    try {
      const data = await getMyProfile();

      // /auth/me carries the organization's feature switches — keep the cached
      // map fresh from the profile call the app already makes.
      setOrgFeatures(data.features);

      setUser({
        ...data,
        email: data.email ?? undefined,
        banner: data.banner ?? undefined,
        phone: data.phone ?? undefined,
        country: data.country ?? undefined,
        role: data.role ?? undefined,
      });
    } catch (error) {
      console.error("refreshUser error", error);
    }
  }, []);

  useEffect(() => {
    if (!getAuthToken()) return;
    refreshUser();
  }, [refreshUser]);

  return (
    <UserContext.Provider value={{ user, setUser, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
